import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, LogOut, Lock, Check, RefreshCw, Link2, BarChart3, Package, CreditCard, Plus, Minus, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";

const INPUT =
  "w-full border border-black/15 bg-white px-4 py-3 text-sm rounded-sm focus:outline-none focus:ring-1 focus:ring-volt focus:border-volt transition-colors duration-200";

const PLAN_STYLE = {
  starter: "bg-black/10 text-black/60",
  essential: "bg-volt/15 text-voltdark",
  growth: "bg-ink text-white",
};

export default function PortalDashboard() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [billing, setBilling] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editUrl, setEditUrl] = useState("");
  const [billingBusy, setBillingBusy] = useState("");
  const [slots, setSlots] = useState(5);
  const [preview, setPreview] = useState(null);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const [planChange, setPlanChange] = useState(null);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const token = localStorage.getItem("revora_portal");

  const authed = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/portal/me", authed());
      setData(data);
      const { data: b } = await api.get("/portal/billing/state", authed());
      setBilling(b);
      if (data.account.plan_details.analytics) {
        const { data: an } = await api.get("/portal/analytics", authed());
        setAnalytics(an);
      } else {
        setAnalytics(null);
      }
    } catch {
      localStorage.removeItem("revora_portal");
      navigate("/portal");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!token) return navigate("/portal");
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const billingParam = params.get("billing");
    const sessionId = params.get("session_id");
    if (billingParam === "canceled") {
      toast.info("Checkout canceled — your plan is unchanged.");
      setParams({}, { replace: true });
      return;
    }
    if (billingParam !== "success" || !sessionId) return;
    let cancelled = false;
    let tries = 0;
    const poll = async () => {
      while (!cancelled && tries < 15) {
        tries += 1;
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          if (data.payment_status === "paid") {
            toast.success("Subscription active — your plan has been updated.");
            setParams({}, { replace: true });
            load();
            return;
          }
        } catch { /* keep polling */ }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) toast.info("Still confirming your subscription — refresh in a moment.");
      setParams({}, { replace: true });
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!data) return <div className="min-h-screen bg-mist" />;

  const { account, orders, assets, capacity } = data;
  const plan = account.plan_details;
  const canEdit = plan.can_edit_destination;
  const paidWithSub = ["essential", "growth"].includes(account.plan) && billing?.has_subscription;
  const planRank = { starter: 0, essential: 1, growth: 2 };

  const claim = async () => {
    try {
      const { data } = await api.post("/portal/claim-orders", {}, authed());
      toast.success(data.claimed ? `Linked ${data.claimed} order${data.claimed > 1 ? "s" : ""} to your account` : "No new orders found for this email");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const register = async (assetId) => {
    try {
      await api.post(`/portal/assets/${assetId}/register`, {}, authed());
      toast.success(`${assetId} registered`);
      load();
    } catch (e) {
      if (e?.response?.data?.detail === "registered_capacity_full") {
        toast.error("All slots are in use — add capacity or unregister another product first.");
      } else {
        toast.error(formatApiError(e));
      }
    }
  };

  const unregister = async (assetId) => {
    try {
      await api.post(`/portal/assets/${assetId}/unregister`, {}, authed());
      toast.success(`${assetId} unregistered — it keeps routing to its saved destination`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveDestination = async (assetId) => {
    try {
      const { data } = await api.patch(`/portal/assets/${assetId}/destination`, { destination_url: editUrl }, authed());
      toast.success("Destination updated — the next tap goes to the new page");
      if (data.warning) toast.warning(data.warning);
      setEditId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const startCheckout = async (planKey) => {
    setBillingBusy(planKey);
    try {
      const { data } = await api.post("/portal/billing/checkout", { plan: planKey, origin_url: window.location.origin }, authed());
      if (data.upgraded) {
        toast.success(`You're now on ${planKey === "growth" ? "Growth" : "Essential"} — proration handled by Stripe`);
        load();
      } else {
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setBillingBusy("");
    }
  };

  const previewPlanChange = async (planKey) => {
    setBillingBusy(`downgrade-${planKey}`);
    try {
      const { data } = await api.post("/portal/billing/plan-change/preview", { plan: planKey }, authed());
      setPlanChange(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBillingBusy("");
    }
  };

  const confirmPlanChange = async () => {
    setBillingBusy("confirm-downgrade");
    try {
      const { data } = await api.post("/portal/billing/plan-change/confirm", { plan: planChange.target_plan }, authed());
      toast.success(`Downgrade to ${planChange.target_label} scheduled for ${new Date(data.effective_at).toLocaleDateString()}`);
      setPlanChange(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBillingBusy("");
    }
  };

  const cancelSubscription = async () => {
    setBillingBusy("cancel");
    try {
      const { data } = await api.post("/portal/billing/cancel-subscription", {}, authed());
      toast.success(`Subscription will end on ${new Date(data.effective_at).toLocaleDateString()} — paid features stay until then`);
      setConfirmingCancel(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBillingBusy("");
    }
  };

  const resumeSubscription = async () => {
    setBillingBusy("resume");
    try {
      await api.post("/portal/billing/resume-subscription", {}, authed());
      toast.success("Subscription resumed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBillingBusy("");
    }
  };

  const previewAddon = async () => {
    setBillingBusy("preview");
    try {
      const { data } = await api.post("/portal/billing/addon/preview", { slots: Number(slots) }, authed());
      setPreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBillingBusy("");
    }
  };

  const confirmAddon = async () => {
    setBillingBusy("confirm");
    try {
      await api.post("/portal/billing/addon/confirm", { slots: preview.slots }, authed());
      toast.success(`+${preview.slots} registered-asset slots added to your subscription`);
      setPreview(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBillingBusy("");
    }
  };

  const removeAddon = async () => {
    setBillingBusy("remove");
    try {
      const { data } = await api.post("/portal/billing/addon/schedule-cancel", {}, authed());
      toast.success(`Add-on removal scheduled for ${new Date(data.effective_at).toLocaleDateString()}`);
      setConfirmingRemove(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBillingBusy("");
    }
  };

  const logout = () => {
    localStorage.removeItem("revora_portal");
    navigate("/portal");
  };

  return (
    <div className="min-h-screen bg-mist" data-testid="portal-dashboard">
      <header className="bg-ink text-white">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" /> Site
            </Link>
            <span className="font-display font-black">{account.business_name}</span>
            <span className={`text-[10px] font-bold tracking-[0.15em] uppercase px-2.5 py-1 rounded-full ${PLAN_STYLE[account.plan]}`} data-testid="plan-badge">
              {plan.label} plan
            </span>
          </div>
          <button data-testid="portal-logout" onClick={logout} className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12 space-y-12">
        <section data-testid="portal-plans">
          <h2 className="font-display text-2xl font-black text-ink mb-6 flex items-center gap-2"><CreditCard className="w-5 h-5 text-voltdark" /> Plan & Billing</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { key: "starter", name: "Starter", price: "$0/mo", points: ["Tap-to-review products", "Fixed review URL", "Portal access"] },
              { key: "essential", name: "Essential", price: "$9.99/mo", points: ["3 registered assets", "Destination editing", "Basic tap analytics", "1 business location"] },
              { key: "growth", name: "Growth", price: "$19.99/mo", points: ["10 registered assets", "Everything in Essential", "Staff attribution", "Extended analytics history"] },
            ].map((p) => {
              const current = account.plan === p.key;
              return (
                <div key={p.key} className={`bg-white rounded-sm p-6 border ${current ? "border-volt ring-1 ring-volt" : "border-black/10"}`} data-testid={`plan-card-${p.key}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-display text-lg font-bold text-ink">{p.name}</p>
                    {current && <span className="text-[10px] font-bold tracking-[0.1em] uppercase bg-volt/15 text-voltdark px-2.5 py-1 rounded-full">Current</span>}
                  </div>
                  <p className="font-display text-2xl font-black text-ink mb-4">{p.price}</p>
                  <ul className="space-y-1.5 mb-6">
                    {p.points.map((pt) => (
                      <li key={pt} className="flex items-center gap-1.5 text-xs text-black/55"><Check className="w-3.5 h-3.5 text-voltdark" /> {pt}</li>
                    ))}
                  </ul>
                  {p.key !== "starter" && !current && planRank[p.key] > planRank[account.plan] && (
                    <button
                      data-testid={`upgrade-${p.key}`}
                      onClick={() => startCheckout(p.key)}
                      disabled={!!billingBusy}
                      className="w-full bg-volt text-ink text-sm font-bold py-3 rounded-full hover:bg-voltdark hover:text-white disabled:opacity-40 transition-colors duration-200"
                    >
                      {billingBusy === p.key ? "Redirecting…" : `Upgrade to ${p.name}`}
                    </button>
                  )}
                  {p.key !== "starter" && !current && planRank[p.key] < planRank[account.plan] && billing?.has_subscription && !billing?.pending_plan && !billing?.cancel_at_period_end && (
                    <button
                      data-testid={`downgrade-${p.key}`}
                      onClick={() => previewPlanChange(p.key)}
                      disabled={!!billingBusy}
                      className="w-full border border-black/15 text-sm font-bold py-3 rounded-full hover:border-ink disabled:opacity-40 transition-colors duration-200"
                    >
                      {billingBusy === `downgrade-${p.key}` ? "Calculating…" : `Downgrade to ${p.name} at renewal`}
                    </button>
                  )}
                  {p.key === "starter" && !current && billing?.has_subscription && (
                    <p className="text-[11px] text-black/40 text-center">Cancel below to return to Starter at the end of your paid period.</p>
                  )}
                </div>
              );
            })}
          </div>
          {billing?.pending_plan && (
            <p className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3" data-testid="pending-plan-note">
              Your plan changes to <strong>{billing.pending_plan === "essential" ? "Essential" : "Growth"}</strong> on{" "}
              <strong>{new Date(billing.pending_plan_at).toLocaleDateString()}</strong>. Until then you keep your current plan.
              Assets, destinations and history are never deleted by a plan change.
            </p>
          )}
          {planChange && (
            <div className="mt-4 bg-mist border border-black/10 rounded-sm p-5" data-testid="plan-change-preview">
              <p className="text-sm font-bold text-ink mb-2">Downgrade to {planChange.target_label}</p>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                <div className="flex justify-between"><span className="text-black/50">Due today</span><span className="font-bold text-ink">${planChange.charge_now.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-black/50">From {new Date(planChange.effective_at).toLocaleDateString()}</span><span className="font-bold text-ink">${planChange.target_monthly.toFixed(2)}/mo</span></div>
              </div>
              <p className="text-[11px] text-black/40 mt-3">{planChange.note} Nothing is deleted — any registered assets above the new limit are marked over capacity (routing keeps working) until you unregister products or add capacity.</p>
              <div className="flex gap-2 mt-4">
                <button data-testid="confirm-plan-change" onClick={confirmPlanChange} disabled={!!billingBusy} className="bg-ink text-white text-sm font-bold px-6 py-3 rounded-full hover:bg-voltdark disabled:opacity-40 transition-colors duration-200">
                  {billingBusy === "confirm-downgrade" ? "Scheduling…" : "Confirm downgrade at renewal"}
                </button>
                <button onClick={() => setPlanChange(null)} className="border border-black/15 text-sm font-bold px-6 py-3 rounded-full hover:border-ink transition-colors">Cancel</button>
              </div>
            </div>
          )}
          {billing?.has_subscription && !billing?.cancel_at_period_end && !confirmingCancel && (
            <button data-testid="cancel-subscription-open" onClick={() => setConfirmingCancel(true)} className="mt-4 text-xs font-bold text-red-500 hover:text-red-600 transition-colors">
              Cancel subscription
            </button>
          )}
          {billing?.has_subscription && billing?.cancel_at_period_end && (
            <div className="mt-4 bg-mist border border-black/10 rounded-sm px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="cancel-scheduled">
              <p className="text-xs text-black/60">
                Your subscription ends on <strong>{billing.current_period_end ? new Date(billing.current_period_end).toLocaleDateString() : "the period end"}</strong>.
                Paid features stay until then, then your account moves to Starter. Your assets, destinations and history are never deleted.
              </p>
              <button data-testid="resume-subscription" onClick={resumeSubscription} disabled={!!billingBusy} className="text-xs font-bold bg-ink text-white px-4 py-2 rounded-full hover:bg-voltdark disabled:opacity-40 transition-colors">
                {billingBusy === "resume" ? "Resuming…" : "Resume subscription"}
              </button>
            </div>
          )}
          {confirmingCancel && !billing?.cancel_at_period_end && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-sm p-4 text-xs text-red-700" data-testid="cancel-subscription-confirm">
              Cancel your {plan.label} subscription? Paid features remain until{" "}
              <strong>{billing?.current_period_end ? new Date(billing.current_period_end).toLocaleDateString() : "the end of the paid period"}</strong>,
              then your account moves to Starter. Registered assets above Starter's limit are marked over capacity — nothing is deleted and routing keeps working.
              <div className="flex gap-2 mt-3">
                <button onClick={cancelSubscription} disabled={!!billingBusy} data-testid="confirm-cancel-subscription" className="bg-red-500 text-white font-bold px-4 py-2 rounded-full hover:bg-red-600 disabled:opacity-40">
                  {billingBusy === "cancel" ? "Canceling…" : "Confirm cancellation"}
                </button>
                <button onClick={() => setConfirmingCancel(false)} className="border border-red-300 font-bold px-4 py-2 rounded-full">Keep subscription</button>
              </div>
            </div>
          )}
          <p className="text-[11px] text-black/40 mt-3">Subscriptions are billed monthly by Stripe. Upgrades apply immediately; downgrades and cancellations take effect at the next renewal.</p>
        </section>

        <section data-testid="portal-capacity">
          <h2 className="font-display text-2xl font-black text-ink mb-2">Registered asset capacity</h2>
          <p className="text-xs text-black/45 mb-5 max-w-2xl leading-relaxed">
            Registered assets use your plan-eligible features: destination editing and tap analytics.
            Unregistered products keep routing customers to their saved destinations — they just don't appear in your analytics
            and can't be edited until registered within your capacity.
          </p>
          <div className="bg-white border border-black/10 rounded-sm p-6">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
              <div>
                <p className="font-display text-3xl font-black text-ink" data-testid="capacity-usage">
                  {capacity.registered} <span className="text-black/30 text-xl">of {capacity.total} slots used</span>
                </p>
                <p className="text-xs text-black/45 mt-1">
                  {capacity.plan_limit} from {plan.label}{capacity.addon_slots > 0 ? ` + ${capacity.addon_slots} add-on slot${capacity.addon_slots > 1 ? "s" : ""}` : ""}
                  {capacity.available > 0 && ` · ${capacity.available} available`}
                </p>
              </div>
              <div className="w-full sm:w-64 h-2 bg-mist rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${capacity.over_capacity_count > 0 ? "bg-amber-500" : "bg-volt"}`}
                  style={{ width: `${Math.min(100, capacity.total ? (capacity.registered / capacity.total) * 100 : 0)}%` }}
                />
              </div>
            </div>
            {capacity.over_capacity_count > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 mb-4 flex items-start gap-2" data-testid="over-capacity-warning">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                {capacity.over_capacity_count} registered asset{capacity.over_capacity_count > 1 ? "s are" : " is"} over your current capacity.
                They keep routing to their saved destinations, but destination editing and analytics are paused until you
                unregister products or restore capacity.
              </p>
            )}
            {paidWithSub && (
              <div className="border-t border-black/10 pt-5 mt-2" data-testid="addon-panel">
                {billing?.addon_pending_slots === 0 ? (
                  <p className="text-xs text-black/60 bg-mist rounded-sm px-4 py-3">
                    Your add-on removal is scheduled for <strong>{new Date(billing.addon_effective_at).toLocaleDateString()}</strong>.
                    Your {billing.addon_slots} add-on slot{billing.addon_slots > 1 ? "s" : ""} stay active until then. Your base plan is not affected.
                  </p>
                ) : (
                  <>
                    <div className="flex flex-wrap items-center gap-3">
                      <p className="text-sm font-bold text-ink">Need more slots?</p>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setSlots(Math.max(1, slots - 1))} className="w-8 h-8 rounded-full border border-black/15 flex items-center justify-center hover:border-ink transition-colors" data-testid="slots-minus"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="font-display font-black text-lg w-8 text-center" data-testid="slots-count">{slots}</span>
                        <button onClick={() => setSlots(Math.min(50, slots + 1))} className="w-8 h-8 rounded-full border border-black/15 flex items-center justify-center hover:border-ink transition-colors" data-testid="slots-plus"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <button
                        data-testid="preview-addon"
                        onClick={previewAddon}
                        disabled={!!billingBusy}
                        className="text-xs font-bold bg-ink text-white px-5 py-2.5 rounded-full hover:bg-voltdark disabled:opacity-40 transition-colors duration-200"
                      >
                        {billingBusy === "preview" ? "Calculating…" : `Preview ${slots} extra slot${slots > 1 ? "s" : ""}`}
                      </button>
                      {billing?.addon_slots > 0 && !confirmingRemove && (
                        <button data-testid="remove-addon" onClick={() => setConfirmingRemove(true)} className="text-xs font-bold text-red-500 hover:text-red-600 transition-colors">
                          Remove add-on
                        </button>
                      )}
                    </div>
                    <p className="text-[11px] text-black/40 mt-2">
                      $1.00 per additional slot per month, billed on your existing {plan.label} subscription. Adds asset slots only —
                      your plan and its features stay the same. Cancel anytime; slots stay active until the end of the paid period.
                    </p>
                    {confirmingRemove && (
                      <div className="mt-4 bg-red-50 border border-red-200 rounded-sm p-4 text-xs text-red-700" data-testid="remove-addon-confirm">
                        Remove your {billing.addon_slots} add-on slot{billing.addon_slots > 1 ? "s" : ""}? They stay active until{" "}
                        <strong>{billing.current_period_end ? new Date(billing.current_period_end).toLocaleDateString() : "period end"}</strong>.
                        Your {plan.label} plan is not affected. Assets above your plan limit will be marked over capacity at that date — nothing is deleted and routing keeps working.
                        <div className="flex gap-2 mt-3">
                          <button onClick={removeAddon} disabled={!!billingBusy} data-testid="confirm-remove-addon" className="bg-red-500 text-white font-bold px-4 py-2 rounded-full hover:bg-red-600 disabled:opacity-40">
                            {billingBusy === "remove" ? "Scheduling…" : "Confirm removal"}
                          </button>
                          <button onClick={() => setConfirmingRemove(false)} className="border border-red-300 font-bold px-4 py-2 rounded-full">Keep add-on</button>
                        </div>
                      </div>
                    )}
                    {preview && (
                      <div className="mt-4 bg-mist border border-black/10 rounded-sm p-5" data-testid="addon-preview">
                        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                          <div className="flex justify-between"><span className="text-black/50">Add-on</span><span className="font-bold text-ink">{preview.slots} slots × ${preview.unit_price.toFixed(2)}/{preview.interval}</span></div>
                          <div className="flex justify-between"><span className="text-black/50">Due on next invoice (prorated)</span><span className="font-bold text-ink">${preview.prorated_amount_due.toFixed(2)}</span></div>
                          <div className="flex justify-between"><span className="text-black/50">New monthly total</span><span className="font-bold text-ink">${preview.new_monthly_total.toFixed(2)}/mo</span></div>
                          <div className="flex justify-between"><span className="text-black/50">New registered-asset limit</span><span className="font-bold text-voltdark">{preview.new_asset_limit}</span></div>
                        </div>
                        <p className="text-[11px] text-black/40 mt-3">{preview.billed_note} Takes effect {preview.effective}.</p>
                        <div className="flex gap-2 mt-4">
                          <button
                            data-testid="confirm-addon"
                            onClick={confirmAddon}
                            disabled={!!billingBusy}
                            className="bg-volt text-ink text-sm font-bold px-6 py-3 rounded-full hover:bg-voltdark hover:text-white disabled:opacity-40 transition-colors duration-200"
                          >
                            {billingBusy === "confirm" ? "Confirming…" : `Confirm — $${preview.prorated_amount_due.toFixed(2)} on next invoice`}
                          </button>
                          <button onClick={() => setPreview(null)} className="border border-black/15 text-sm font-bold px-6 py-3 rounded-full hover:border-ink transition-colors">Cancel</button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            {["essential", "growth"].includes(account.plan) && !billing?.has_subscription && (
              <p className="text-xs text-black/60 bg-mist border border-black/10 rounded-sm px-4 py-3 border-t-0 -mt-0" data-testid="no-subscription-note">
                Your {plan.label} plan isn't linked to an active subscription, so capacity add-ons can't be attached yet.
                Contact revorareviewsupport@gmail.com and we'll enable them for your account.
              </p>
            )}
            {account.plan === "starter" && (
              <p className="text-xs text-black/45 border-t border-black/10 pt-4 mt-2">
                Starter has no registered-asset slots. Upgrade to Essential or Growth to register products for destination editing and analytics.
              </p>
            )}
          </div>
        </section>

        <section data-testid="portal-orders">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <h2 className="font-display text-2xl font-black text-ink flex items-center gap-2"><Package className="w-5 h-5 text-voltdark" /> My Orders</h2>
            <button data-testid="claim-orders-button" onClick={claim} className="inline-flex items-center gap-2 text-xs font-bold bg-ink text-white px-5 py-2.5 rounded-full hover:bg-voltdark transition-colors duration-200">
              <Link2 className="w-3.5 h-3.5" /> Link orders placed with {account.email}
            </button>
          </div>
          {orders.length === 0 ? (
            <div className="bg-white border border-black/10 rounded-sm p-10 text-center" data-testid="orders-empty">
              <p className="text-black/50 text-sm">No orders linked yet. Orders placed with <strong className="text-ink">{account.email}</strong> can be linked securely using the button above.</p>
            </div>
          ) : (
            <div className="bg-white border border-black/10 rounded-sm overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="text-left text-xs font-bold tracking-[0.15em] uppercase text-black/40 border-b border-black/10">
                    <th className="px-5 py-4">Order</th><th className="px-5 py-4">Product</th><th className="px-5 py-4">Status</th><th className="px-5 py-4">Assets</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.order_number} className="border-b border-black/5 last:border-0" data-testid={`portal-order-${o.order_number}`}>
                      <td className="px-5 py-4 font-bold text-ink">{o.order_number}</td>
                      <td className="px-5 py-4 text-xs">{o.package_name || o.package_id}</td>
                      <td className="px-5 py-4 text-xs">{o.status}</td>
                      <td className="px-5 py-4 text-xs">{o.assigned_assets?.length || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section data-testid="portal-assets">
          <h2 className="font-display text-2xl font-black text-ink mb-2 flex items-center gap-2"><RefreshCw className="w-5 h-5 text-voltdark" /> My Revora Products</h2>
          <p className="text-xs text-black/45 mb-6">
            {canEdit
              ? "Register products into your available slots to edit their destination and see their tap analytics."
              : "Starter includes a fixed review URL. Upgrade to Essential or Growth to register products and edit destinations."}
          </p>
          {assets.length === 0 ? (
            <div className="bg-white border border-black/10 rounded-sm p-10 text-center" data-testid="assets-empty">
              <p className="text-black/50 text-sm">No products assigned yet. Once Revora assigns assets to your order, they appear here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {assets.map((a) => (
                <motion.div key={a.asset_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-black/10 rounded-sm p-5" data-testid={`portal-asset-${a.asset_id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <p className="font-bold text-ink flex items-center gap-2 flex-wrap">
                        {a.asset_id}
                        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-black/40">{a.status}</span>
                        {a.registered && !a.over_capacity && (
                          <span className="text-[10px] font-bold uppercase tracking-[0.1em] bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Registered</span>
                        )}
                        {a.over_capacity && (
                          <span className="text-[10px] font-bold uppercase tracking-[0.1em] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full" data-testid={`over-capacity-${a.asset_id}`}>Over capacity</span>
                        )}
                      </p>
                      <p className="text-[11px] text-black/40 font-mono">{a.permanent_url}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {a.registered ? (
                        <button data-testid={`unregister-${a.asset_id}`} onClick={() => unregister(a.asset_id)} className="text-xs font-bold text-black/50 hover:text-ink transition-colors">
                          Unregister
                        </button>
                      ) : canEdit && (
                        <button data-testid={`register-${a.asset_id}`} onClick={() => register(a.asset_id)} className="text-xs font-bold bg-ink text-white px-4 py-2 rounded-full hover:bg-voltdark transition-colors duration-200">
                          Register
                        </button>
                      )}
                      {canEdit && a.covered ? (
                        <button
                          data-testid={`edit-destination-${a.asset_id}`}
                          onClick={() => { setEditId(editId === a.asset_id ? null : a.asset_id); setEditUrl(a.destination_url || ""); }}
                          className="text-xs font-bold text-voltdark hover:text-ink transition-colors"
                        >
                          Edit destination
                        </button>
                      ) : canEdit && a.registered && a.over_capacity ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-amber-600"><Lock className="w-3.5 h-3.5" /> Editing paused</span>
                      ) : !canEdit ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-black/35"><Lock className="w-3.5 h-3.5" /> Fixed on Starter</span>
                      ) : null}
                    </div>
                  </div>
                  <p className="text-xs text-black/55 mt-2 break-all">
                    Destination: {a.destination_url || "not configured"}
                    {!a.registered && <span className="text-black/40"> — routing active; register to manage it</span>}
                    {a.over_capacity && <span className="text-amber-600"> — routing active; editing & analytics paused while over capacity</span>}
                  </p>
                  {editId === a.asset_id && canEdit && a.covered && (
                    <div className="mt-4 flex gap-2">
                      <input data-testid={`destination-input-${a.asset_id}`} className={INPUT} value={editUrl} onChange={(e) => setEditUrl(e.target.value)} placeholder="https://g.page/r/…/review" />
                      <button data-testid={`save-destination-${a.asset_id}`} onClick={() => saveDestination(a.asset_id)} className="shrink-0 inline-flex items-center gap-2 bg-volt text-ink text-xs font-bold px-5 py-3 rounded-full hover:bg-voltdark hover:text-white transition-colors duration-200">
                        <Check className="w-3.5 h-3.5" /> Save
                      </button>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
          {canEdit && capacity.available === 0 && assets.some((a) => !a.registered) && (
            <div className="mt-4 bg-white border border-black/10 rounded-sm p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="capacity-full-options">
              <p className="text-sm text-black/60">
                {billing?.has_subscription
                  ? "All your slots are in use. Add capacity or upgrade your plan — your choice."
                  : "All your slots are in use. Upgrade your plan, or contact us to enable capacity add-ons for this account."}
              </p>
              <div className="flex gap-2 shrink-0">
                {billing?.has_subscription && (
                  <button onClick={() => document.querySelector('[data-testid="portal-capacity"]')?.scrollIntoView({ behavior: "smooth" })} className="text-xs font-bold bg-ink text-white px-4 py-2.5 rounded-full hover:bg-voltdark transition-colors" data-testid="capacity-full-addon">
                    Add capacity
                  </button>
                )}
                <button onClick={() => document.querySelector('[data-testid="portal-plans"]')?.scrollIntoView({ behavior: "smooth" })} className="text-xs font-bold border border-black/15 px-4 py-2.5 rounded-full hover:border-ink transition-colors" data-testid="capacity-full-upgrade">
                  View plans
                </button>
              </div>
            </div>
          )}
        </section>

        <section data-testid="portal-analytics">
          <h2 className="font-display text-2xl font-black text-ink mb-6 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-voltdark" /> Tap Analytics</h2>
          {plan.analytics && analytics ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="analytics-real">
              <div className="bg-white border border-black/10 rounded-sm p-5">
                <p className="font-display text-3xl font-black text-ink">{analytics.total_interactions}</p>
                <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-black/40 mt-1">Total taps</p>
              </div>
              <div className="bg-white border border-black/10 rounded-sm p-5">
                <p className="font-display text-3xl font-black text-ink">{Object.keys(analytics.daily_last_30_days).length}</p>
                <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-black/40 mt-1">Active days (30d)</p>
              </div>
              <div className="bg-white border border-black/10 rounded-sm p-5 col-span-2">
                <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-black/40 mb-2">Taps per registered product</p>
                {Object.entries(analytics.per_asset).map(([id, n]) => (
                  <p key={id} className="text-xs text-black/60"><strong className="text-ink">{id}</strong> — {n} taps</p>
                ))}
                {Object.keys(analytics.per_asset).length === 0 && <p className="text-xs text-black/40">No registered products yet — register products above to see their taps here.</p>}
              </div>
              <p className="col-span-full text-[11px] text-black/40">{analytics.note} Only registered, in-capacity products are included.</p>
            </div>
          ) : (
            <div className="relative bg-white border border-black/10 rounded-sm p-8 overflow-hidden" data-testid="analytics-locked">
              <div className="absolute top-3 right-3 bg-amber-100 text-amber-700 text-[10px] font-bold tracking-[0.1em] uppercase px-2.5 py-1 rounded-full">Sample preview — not your data</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 opacity-60 select-none" aria-hidden="true">
                {[["Total taps", "128"], ["Active days (30d)", "14"], ["This week", "23"], ["Products", "3"]].map(([l, v]) => (
                  <div key={l} className="bg-mist border border-dashed border-black/15 rounded-sm p-5">
                    <p className="font-display text-3xl font-black text-black/25">{v}</p>
                    <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-black/25 mt-1">{l}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-sm text-black/55 max-w-md">
                  <Lock className="w-4 h-4 inline mr-1.5 text-voltdark" />
                  Analytics are available on <strong className="text-ink">Essential</strong> and <strong className="text-ink">Growth</strong>.
                  The figures above are labeled sample values only. Revora reports taps, not confirmed Google reviews.
                </p>
                <button
                  onClick={() => document.querySelector('[data-testid="portal-plans"]')?.scrollIntoView({ behavior: "smooth" })}
                  data-testid="upgrade-cta"
                  className="shrink-0 inline-flex items-center gap-2 bg-ink text-white text-sm font-bold px-6 py-3 rounded-full hover:bg-voltdark transition-colors duration-200"
                >
                  Upgrade plan
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
